import { useConsoleDebugStore } from '@/stores/consoleDebugStore';
import { isBenignGlobalErrorMessage } from '@/utils/benignErrors';

// Store original console methods
const originalConsole = {
  error: console.error,
  warn: console.warn,
  log: console.log,
  info: console.info,
};

// Helper to safely stringify objects with circular references
const safeStringify = (obj: any, maxDepth = 3): string => {
  const seen = new WeakSet();

  const stringify = (value: any, depth = 0): string => {
    if (depth > maxDepth) return '[Max depth reached]';

    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'function') return '[Function]';
    if (value instanceof Date) return value.toISOString();
    if (value instanceof RegExp) return value.toString();

    if (typeof value === 'object') {
      if (seen.has(value)) return '[Circular Reference]';
      seen.add(value);

      if (Array.isArray(value)) {
        const items = value.slice(0, 10).map((item) => stringify(item, depth + 1));
        if (value.length > 10) items.push('...');
        return `[${items.join(', ')}]`;
      }

      // Handle DOM elements
      if (value instanceof Element) {
        return `<${value.tagName.toLowerCase()}${value.id ? ` id="${value.id}"` : ''}${value.className ? ` class="${value.className}"` : ''}>`;
      }

      // Handle regular objects
      try {
        const keys = Object.keys(value).slice(0, 10);
        const pairs = keys.map((key) => `${key}: ${stringify(value[key], depth + 1)}`);
        if (Object.keys(value).length > 10) pairs.push('...');
        return `{${pairs.join(', ')}}`;
      } catch {
        return Object.prototype.toString.call(value);
      }
    }

    return String(value);
  };

  return stringify(obj);
};

// Helper to extract error information with better error handling
const extractErrorInfo = (args: any[]): { message: string; stack?: string; details: any[] } => {
  if (!args || args.length === 0) {
    return { message: 'Empty console call', stack: undefined, details: [] };
  }

  let message = '';
  let stack: string | undefined;
  const details: any[] = [];

  for (const arg of args) {
    try {
      if (arg instanceof Error) {
        message += arg.message + ' ';
        stack = arg.stack;
        details.push({
          type: 'Error',
          name: arg.name,
          message: arg.message,
          stack: arg.stack,
        });
      } else if (typeof arg === 'string') {
        message += arg + ' ';
      } else if (arg === null) {
        message += 'null ';
      } else if (arg === undefined) {
        message += 'undefined ';
      } else if (typeof arg === 'object') {
        const stringified = safeStringify(arg);
        message += stringified + ' ';

        // Only add to details if it's a meaningful object
        if (stringified !== '[object Object]' && stringified !== '{}') {
          details.push(arg);
        }
      } else {
        // Primitives (number, boolean, etc.)
        message += String(arg) + ' ';
      }
    } catch (error) {
      // Fallback for any unexpected errors
      message += '[Error processing argument] ';
      console.warn('Error in extractErrorInfo:', error);
    }
  }

  return {
    message: message.trim() || 'Unknown console message',
    stack,
    details: details.length > 0 ? details : [],
  };
};

// Get source location from stack trace
const getSourceFromStack = (stack?: string): string | undefined => {
  if (!stack) return undefined;

  const lines = stack.split('\n');
  // Find the first line that's not from console interceptor
  for (const line of lines) {
    if (
      !line.includes('consoleInterceptor') &&
      !line.includes('console.') &&
      line.includes('at ')
    ) {
      // Extract file and line number
      const match = line.match(/at\s+(?:.*?\s+)?\(?([^)]+)\)?/);
      if (match && match[1]) {
        // Clean up the path to be more readable
        const path = match[1];
        const parts = path.split('/');
        if (parts.length > 2) {
          return parts.slice(-2).join('/');
        }
        return path;
      }
    }
  }
  return undefined;
};

export const initializeConsoleInterceptor = () => {
  // Intercept console.error
  console.error = (...args: any[]) => {
    originalConsole.error(...args);

    const store = useConsoleDebugStore.getState();
    if (store.isEnabled) {
      const { message, stack, details } = extractErrorInfo(args);
      const source = getSourceFromStack(new Error().stack);
      store.addEntry({
        level: 'error',
        message,
        stack,
        details: details.length > 0 ? details : undefined,
        source,
      });
    }
  };

  // Intercept console.warn
  console.warn = (...args: any[]) => {
    originalConsole.warn(...args);

    const store = useConsoleDebugStore.getState();
    if (store.isEnabled) {
      const { message, stack, details } = extractErrorInfo(args);
      const source = getSourceFromStack(new Error().stack);
      store.addEntry({
        level: 'warn',
        message,
        stack,
        details: details.length > 0 ? details : undefined,
        source,
      });
    }
  };

  // Optionally intercept console.log and console.info (disabled by default to avoid noise)
  // Uncomment if you want to track all console output
  /*
  console.log = (...args: any[]) => {
    originalConsole.log(...args);
    
    const store = useConsoleDebugStore.getState();
    if (store.isEnabled) {
      const { message, details } = extractErrorInfo(args);
      const source = getSourceFromStack(new Error().stack);
      store.addEntry({
        level: 'log',
        message,
        details: details.length > 0 ? details : undefined,
        source,
      });
    }
  };

  console.info = (...args: any[]) => {
    originalConsole.info(...args);
    
    const store = useConsoleDebugStore.getState();
    if (store.isEnabled) {
      const { message, details } = extractErrorInfo(args);
      const source = getSourceFromStack(new Error().stack);
      store.addEntry({
        level: 'info',
        message,
        details: details.length > 0 ? details : undefined,
        source,
      });
    }
  };
  */

  // Intercept window error events
  window.addEventListener('error', (event) => {
    if (isBenignGlobalErrorMessage(event.message)) {
      return;
    }

    const store = useConsoleDebugStore.getState();
    if (store.isEnabled) {
      store.addEntry({
        level: 'error',
        message: event.message,
        stack: event.error?.stack,
        source: `${event.filename}:${event.lineno}:${event.colno}`,
      });
    }
  });

  // Intercept unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const store = useConsoleDebugStore.getState();
    if (store.isEnabled) {
      let message = 'Unhandled Promise Rejection: ';
      let stack: string | undefined;
      let details: any[] | undefined;

      if (event.reason instanceof Error) {
        message += event.reason.message;
        stack = event.reason.stack;
      } else {
        message += String(event.reason);
        if (typeof event.reason === 'object') {
          details = [event.reason];
        }
      }

      store.addEntry({
        level: 'error',
        message,
        stack,
        details,
      });
    }
  });
};

// Restore original console methods (useful for cleanup)
export const restoreConsole = () => {
  console.error = originalConsole.error;
  console.warn = originalConsole.warn;
  console.log = originalConsole.log;
  console.info = originalConsole.info;
};
